import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../../assets/icons/sidebar/sidebar-payment.svg';
import { ReactComponent as SlashIcon } from '../../../assets/icons/slash.svg';
import { ReactComponent as StopIcon } from '../../../assets/icons/stop-circle.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { CommercialSessionInterface } from '../../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableSessionFiltersApplied,
  fetchAllSessions,
  fetchListSessionFilters,
  updateSessionFilters,
  setSessionSortData,
  refreshChargingSessions,
} from '../../../redux/slices/ChargingSessionSlice';
// import { setSelectedDevice } from '../../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { setSidePanelData } from '../../../redux/slices/TableSidePanelSlice';
import CustomActionBar from '../../../shared-components/custom-action-bar/CustomActionBar';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
// import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
// import DeviceViewSidePanel from '../../../shared-components/table-side-panel/DeviceViewSidePanel';
import LocationSidePanel from '../../../shared-components/table-side-panel/LocationSidePanel';
import TariffSidePanel from '../../../shared-components/table-side-panel/TariffSidePanel';
// import StationViewSidePanel from '../../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { changeDateFormat, getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import occpEvent from '../../../utils/api/ocpp-events';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element | null;
  rightCorner: string;
  notification: boolean;
}

/**
 * Commercial Session Table Component
 * @param {any} param0 - The param0
 * @param {any} param0.currentPage - The currentPage prop
 * @param {any} param0.setCurrentPage - The setCurrentPage prop
 */
function CommercialSessionTable({
  currentPage,
  setCurrentPage,
}: {
  currentPage: string;
  setCurrentPage: (value: string) => void;
}) {
  const { t, i18n } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  // @ts-ignore
  const {
    payments: sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
    TotalFinising,
    totalInprocess,
    totalterminated,
    // @ts-ignore
  } = useAppSelector((state) => state.chargingSession);
  // const [isPolling, setIsPolling] = useState<boolean>(false);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [activeStatus, setActiveStatus] = useState<string>('');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const isStatusClicked = React.useRef(false);
  const pageReference = React.useRef<number>(page || 1);

  useEffect(() => {
    if (page) {
      pageReference.current = page;
    }
  }, [page]);

  const currentDate = changeDateFormat(new Date().toString());
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: currentDate,
    endDate: currentDate,
  });
  const [isAutoRefreshActive, setIsAutoRefreshActive] = useState<boolean>(true);

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
    setIsAutoRefreshActive(false);
  };

  /**
   * Handle date range change.
   * @param {string} start - The start date string.
   * @param {string} end - The end date string.
   */
  const handleDateRangeChange = (start: string, end: string) => {
    const today = changeDateFormat(new Date().toString());
    setDateRange({ startDate: start, endDate: end });
    if (start !== today || end !== today) {
      setIsAutoRefreshActive(false);
    }
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={handleDateRangeChange}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          disableDates="future"
        />
      </div>
    );
  };

  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    content: null,
    header: true,
    rightCorner: '',
    notification: false,
  });
  const { portalType } = usePortal();
  const [searchParameters, setSearchParameters] = useSearchParams();

  /**
   * Fetch the session details in redux
   * @param {any} data - The data
   * @param {boolean} [force] - Whether to force fetch even if loading
   */
  const fetchSessions = (data: any, force = false) => {
    if (getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading || force) {
      dispatch(
        fetchAllSessions({
          ...data,
          ...(activeStatus ? { status: activeStatus } : {}),
        }),
      );
    }
  };

  /**
   * Function to refresh data
   * @param {boolean} [force] - Whether to force refresh
   */
  const handleRefresh = (force = false) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions(
      {
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page,
        limit,
        ...sortValue,
      },
      force,
    );
  };

 

  /**
   * Handle edit mode state change
   * @param {boolean} editMode - Edit mode boolean
   */
  const handleEditModeChange = (editMode: boolean) => {
    setIsEditMode(editMode);
  };

  /**
   * Format duration in seconds to h m s format
   * @param {number | undefined | null} seconds - The duration in seconds
   * @returns {string} The formatted duration string
   */
  const formatDuration = (seconds: number | undefined | null): string => {
    if (seconds === undefined || seconds === null || seconds <= 0) {
      return '-';
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    const result: string[] = [];
    if (h > 0) result.push(`${h}h`);
    if (m > 0) result.push(`${m}m`);
    if (s > 0 || result.length === 0) result.push(`${s}s`);
    return result.join(' ');
  };

  /**
   * Function to handle radio button change
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentPage(value);
  };

 

  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const showSuccessNotification = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
              handleRefresh(true);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };

  /**
   * Function to handle stop button click
   * @param {any} data session data or list of ids
   */
  const handleStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session: any) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsStopRequestSuccess')
            : t('transactionStopRequestSuccess'),
        );
        handleRefresh(true);
        setTimeout(() => {
          handleRefresh(true);
        }, 1500);
        setTimeout(() => {
          handleRefresh(true);
        }, 3500);
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to handle force stop button click
   * @param {any} data session data or list of ids
   */
  const handleForceStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter(
        (session: any) => data.includes(session.id) || data.includes(session._id),
      );
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId || item.chargingTransactionId,
        forceStop: 'true',
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsForceStopRequestSuccess')
            : t('transactionForceStopRequestSuccess'),
        );
        handleRefresh(true);
        setTimeout(() => {
          handleRefresh(true);
        }, 1500);
        setTimeout(() => {
          handleRefresh(true);
        }, 3500);
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  const column = [
    {
      label: t('OCPP Transaction Id'),
      accessorKey: 'transactionId' as keyof CommercialSessionInterface,
    },

    {
      label: t('sessionStartTime'),
      accessorKey: 'transactionStartsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging started at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData?.transactionStartsAt as any)}</span>
      ),
    },
    {
      label: t('SessionEndTime'),
      accessorKey: 'transactionEndsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging ended at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData?.transactionEndsAt as any)}</span>
      ),
    },
    {
      label: t('PartnerName'),
      accessorKey: 'facilityAccountName' as keyof CommercialSessionInterface,
      sortAllow: false,
    },

    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof CommercialSessionInterface,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof CommercialSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn' as keyof CommercialSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('tariffId'),
      accessorKey: 'tariff' as keyof CommercialSessionInterface,
      sortAllow: false,
      queryParamsKey: 'tariffId',
      sidePanelComponent: <TariffSidePanel fetchIdFrom="queryParams" isPartner={false} />,
      disableNewTab: true,
      /**
       * Custom data for the tariff ID column
       * @param {CommercialSessionInterface} rowData - Session data
       * @returns {any} Tariff ID element
       */
      customData: (rowData: CommercialSessionInterface) => {
        const tariffId = rowData?.tariff?.tariffId || (rowData?.tariff as any)?._id;

        if (!tariffId) {
          return <span>-</span>;
        }

        return (
          <span
            className="table-row-each-data hyperlink"
            role="presentation"
            onClick={(event) => {
              event.stopPropagation();
              const parameters = new URLSearchParams(searchParameters);
              const sidePanelKeys = [
                'accountCustomId',
                'deviceId',
                'connectorDeviceId',
                'stationId',
                'facilityId',
                'userCustomId',
                'userId',
                'utilityId',
                'transactionObjectId',
                'roleId',
                'sessionId',
                'cdrObjectId',
                'paymentDetailsId',
                'personalChargerId',
                'tariffId',
                'action',
              ];
              sidePanelKeys.forEach((key) => parameters.delete(key));
              parameters.set('tariffId', tariffId);
              setSearchParameters(parameters);
              dispatch(
                setSidePanelData({
                  key: 'Sessions',
                  value: rowData.id,
                }),
              );
            }}>
            {tariffId}
          </span>
        );
      },
    },
    {
      label: t('ocpiId'),
      accessorKey: 'ocpiId' as keyof CommercialSessionInterface,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh' as keyof CommercialSessionInterface,
      /**
       * Custom data for the power consumed column
       * @param {CommercialSessionInterface} rowData session data
       * @returns {React.ReactElement} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        const energy =
          rowData.powerConsumedInKwh ?? rowData.energyKwh ?? (rowData as any).energyConsumedKwh;
        return (
          <span>
            {energy !== undefined && energy !== null && energy !== ''
              ? Number(energy).toFixed(3)
              : ''}
          </span>
        );
      },
    },
    {
      label: t('amount'),
      accessorKey: 'totalAmount' as keyof CommercialSessionInterface,
      /**
       * Custom data for the total amount column
       * @param {CommercialSessionInterface} rowData session data
       * @returns {React.ReactElement} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        const amount =
          rowData.totalAmount ?? rowData.totalCost ?? (rowData as any).amount;
        return (
          <span>
            {amount !== undefined && amount !== null && amount !== ''
              ? `${Number(amount).toFixed(2)} ${rowData?.currency || ''}`
              : ''}
          </span>
        );
      },
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof CommercialSessionInterface,
      /**
       * Custom data for the duration column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{formatDuration(rowData.durationSeconds) || '-'}</span>
      ),
    },
    {
      label: t('userName'),
      accessorKey: 'userCustomId' as keyof CommercialSessionInterface,
      sortAllow: false,
      /**
       * Custom data for user name column
       * @param {CommercialSessionInterface} rowData - Session data
       * @returns {any} User name element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{rowData?.userCustomId || (rowData as any)?.userName || rowData?.endConsumer || '-'}</span>
      ),
    },

    {
      label: t('status'),
      accessorKey: 'status' as keyof CommercialSessionInterface,
      /**
       * Custom data for the status column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        let statusClass = '';
        let statusKey = 'na';
        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
            statusKey = 'finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
            statusKey = 'inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
            statusKey = 'pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
            statusKey = 'terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
            statusKey = 'failed';
          } else {
            statusKey = rowData.status;
          }
        }
        return (
          <span className={`status-badge ${statusClass}`}>
            <span className="status-text capitalize">
              {t(statusKey, { defaultValue: rowData?.status || t('na') })}
            </span>
          </span>
        );
      },
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof CommercialSessionInterface,
      sticky: 'right' as const,
      /**
       * Custom data for the actions column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        if (rowData?.status === 'inProgress') {
          return (
            <div className="stop-buttons">
              <span role="presentation" title={t('stop')}>
                <StopIcon onClick={() => handleStop(rowData)} />
              </span>
              <span role="presentation" title={t('forceStop')}>
                <SlashIcon onClick={() => handleForceStop(rowData)} />
              </span>
            </div>
          );
        }
        return <div>{t('na')}</div>;
      },
    },
  ];

  /**
   * Table pagination function
   * @param {any} value page and limit values
   */
  const pageNateLimitChange = (value: any) => {
    if (value?.page) {
      pageReference.current = value.page;
    }
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      ...sortValue,
    });
  };

  /**
   * Handle auto refresh toggle change
   * @param {boolean} nextState - Next auto refresh state
   */
  const handleAutoRefreshToggle = (nextState: boolean) => {
    setIsAutoRefreshActive(nextState);
    if (nextState) {
      const today = changeDateFormat(new Date().toString());
      if (dateRange.startDate !== today || dateRange.endDate !== today) {
        setDateRange({
          startDate: today,
          endDate: today,
        });
      }
    }
  };

  /**
   * Function to auto refresh
   */
  const handleAutoRefresh = () => {
    const today = changeDateFormat(new Date().toString());
    if (dateRange.startDate !== today || dateRange.endDate !== today) {
      return;
    }
    const queryParameters = GetParametersFromCommonFilters(filters);
    const activePage = pageReference.current || page || 1;

    dispatch(
      refreshChargingSessions({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: activePage,
        limit,
        sortBy,
        sortField,
        ...(activeStatus ? { status: activeStatus } : {}),
      }),
    );
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    {
      label: t('OCPP Transaction Id'),
      accessorKey: 'transactionId',
    },
    {
      label: t('sessionStartTime'),
      accessorKey: 'transactionStartsAt',
    },
    {
      label: t('SessionEndTime'),
      accessorKey: 'transactionEndsAt',
    },
    {
      label: t('PartnerName'),
      accessorKey: 'facilityAccountName',
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStation',
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName',
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn',
    },
    {
      label: t('tariffId'),
      accessorKey: 'tariff',
    },
    {
      label: t('ocpiId'),
      accessorKey: 'ocpiId',
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh',
    },
    {
      label: t('amount'),
      accessorKey: 'totalAmount',
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds',
    },
    {
      label: t('userName'),
      accessorKey: 'userCustomId',
    },
    {
      label: t('status'),
      accessorKey: 'status',
    },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function to export data in PDF or CSV format
   * @param {string} type - The export format type ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    if (
      dateRange.startDate &&
      dateRange.endDate &&
      getDaysBetween(dateRange.startDate, dateRange.endDate) > 31
    ) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    const headers = exportCurrentColumns.map((col) => col.label);

    const rows = sessions.map((row: CommercialSessionInterface) =>
      exportCurrentColumns.map((col) => {
        switch (col.accessorKey) {
          case 'transactionStartsAt':
            return row.transactionStartsAt ? ConvertUTCtoDateTime(row.transactionStartsAt as any) : 'N/A';

          case 'transactionEndsAt':
            return row.transactionEndsAt ? ConvertUTCtoDateTime(row.transactionEndsAt as any) : 'N/A';

          case 'status':
            return row.status ? t(row.status) : 'N/A';

          case 'durationSeconds':
            return formatDuration(row.durationSeconds) || 'N/A';

          case 'tariff':
            return (
              row?.tariff?.tariffId || (row?.tariff as any)?._id || (row as any)?.tariffId || 'N/A'
            );

          case 'facilityAccountName':
            return (row as any)?.facilityAccountName || row?.accountCustomId || 'N/A';

          case 'userCustomId':
            return row?.userCustomId || (row as any)?.userName || row?.endConsumer || 'N/A';

          case 'powerConsumedInKwh': {
            const energy =
              row?.powerConsumedInKwh ?? row?.energyKwh ?? (row as any)?.energyConsumedKwh;
            return energy !== undefined && energy !== null && energy !== ''
              ? Number(energy).toFixed(3)
              : 'N/A';
          }

          case 'totalAmount': {
            const amount =
              row?.totalAmount ?? row?.totalCost ?? (row as any)?.amount;
            return amount !== undefined && amount !== null && amount !== ''
              ? Number(amount).toFixed(2)
              : 'N/A';
          }

          default: {
            const fieldValue = (row as any)[col.accessorKey];
            if (fieldValue && typeof fieldValue === 'object') {
              return fieldValue.name || fieldValue.id || fieldValue.tariffId || 'N/A';
            }
            return fieldValue ?? 'N/A';
          }
        }
      }),
    );

    if (rows.length > 0) {
      if (type === 'csv') {
        exportCSV(headers, rows, 'commercial-sessions.csv');
      } else {
        exportPDF(headers, rows, 'commercial-sessions.pdf');
      }
    }
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    pageReference.current = DefaultApiLimit.page;
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSessionSortData(sortData));
    fetchSessions({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Poll session data every 10 seconds
   */
  // useEffect(() => {
  //   // let timeout: NodeJS.Timeout;
  //   // if (isPolling) {
  //   //   timeout = setTimeout(() => {
  //   //     handleRefresh();
  //   //     setIsPolling(false);
  //   //   }, 10000);
  //   // }
  //   // return () => clearTimeout(timeout);
  // }, [isPolling]);

  const isFirstMountFilters = React.useRef(true);

  useEffect(() => {
    if (isFirstMountFilters.current) {
      isFirstMountFilters.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchSessions({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
      return;
    }

    isStatusClicked.current = false;
    pageReference.current = DefaultApiLimit.page;
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchSessions({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: DefaultApiLimit.page,
      limit,
    });

    if (isFilterUpdated) {
      dispatch(disableSessionFiltersApplied());
    }
  }, [filters, activeStatus, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('sessions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  /**
   * Check if status filter is currently active
   * @param {string} statusValue - The status value to check
   * @returns {boolean} True if active
   */
  const isStatusActive = (statusValue: string): boolean => {
    return activeStatus === statusValue;
  };

  /**
   * Handle status badge click to apply/toggle filter
   * @param {string} statusValue - The status value to filter by
   */
  const handleStatusClick = (statusValue: string) => {
    isStatusClicked.current = true;
    setActiveStatus((previous) => (previous === statusValue ? '' : statusValue));
  };

  return (
    <div className="payment-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <div className="stats-bar">
        <div className="stat-item" key="total">
          <span className="stat-label">{t('total')}</span>
          <span className="stat-value">{totalResults ?? 0}</span>
        </div>
        <div className="stat-divider" />
        <div
          className={`stat-badge finished-badge ${isStatusActive('finished') ? 'active' : ''}`}
          key="finished"
          role="presentation"
          onClick={() => handleStatusClick('finished')}>
          <span className="badge-dot" />
          {t('finished')}: {TotalFinising ?? 0}
        </div>
        <div
          className={`stat-badge inProgress-badge ${isStatusActive('inProgress') ? 'active' : ''}`}
          key="inProgress"
          role="presentation"
          onClick={() => handleStatusClick('inProgress')}>
          <span className="badge-dot" />
          {t('inProgress')}: {totalInprocess ?? 0}
        </div>
        <div
          className={`stat-badge terminated-badge ${isStatusActive('terminated') ? 'active' : ''}`}
          key="terminated"
          role="presentation"
          onClick={() => handleStatusClick('terminated')}>
          <span className="badge-dot" />
          {t('terminated')}: {totalterminated ?? 0}
        </div>
      </div>
      <div className="table-content">
        <CustomTable
          dataKey="CommercialSessions"
          version="v2"
          header={column}
          data={sessions}
          limit={limit}
          page={pageReference.current || page || 1}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          exportDataPdfOrCSV={
            permissionCheck('partnerSessionExport') || permissionCheck('cpoSessionExport')
              ? exportDataPdfOrCSV
              : undefined
          }
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateSessionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListSessionFilters}
          rightTopContent={
            <>
              {portalType === PortalType.NETWORKS && (
                <div className="revenue-management-toggle-wrap">
                  <CustomRadioButton
                    name=""
                    value="commercial"
                    label={t('commercialCharger')}
                    selected={currentPage === 'commercial'}
                    onChangeFunc={handleAvailabilityChange}
                  />
                  <CustomRadioButton
                    name=""
                    value="personal"
                    label={t('personalCharger')}
                    selected={currentPage === 'personal'}
                    onChangeFunc={handleAvailabilityChange}
                  />
                </div>
              )}
              {renderDateFilters()}
            </>
          }
          onHiddenColumnsChange={setHiddenColumns}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          checkbox
          autoRefreshEnabled
          isAutoRefreshActive={isAutoRefreshActive}
          onAutoRefreshChange={handleAutoRefreshToggle}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          selectKey="id"
          isRowSelectable={(row) => row.status === 'inProgress'}
          onClearFilterChange={() => {
            clearDateRange();
          }}
          height={320}
        />
        <CustomActionBar
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          actions={[
            {
              label: t('stop'),
              // icon: <StopIcon />,
              buttonType: 'danger-outline',
              /**
               * handle stop
               * @todo
               */
              onClick: () => handleStop(selectedRows),
            },
            {
              label: t('forceStop'),
              // icon: <SlashIcon />,
              buttonType: 'error',
              /**
               * handle force stop
               * @todo
               */
              onClick: () => handleForceStop(selectedRows),
            },
          ]}
        />
      </div>
    </div>
  );
}

export default CommercialSessionTable;
